/**
 * Task Visibility Utility
 * =======================
 * Determines which tasks are visible/unlocked for clients based on
 * program organization type and assignment start date.
 */

import type { ProgramItemWithProgress } from '../services/assignmentService'
import type { ProgramOrganizationType } from '../types/program-scheduling.types'

/**
 * Extended program item type combining progress and scheduling
 */
export interface ScheduledTaskItem extends ProgramItemWithProgress {
  scheduled_day: number | null
  scheduled_week: number | null
  scheduled_time: string | null
}

/**
 * Task visibility status
 */
export type TaskStatus = 'available' | 'locked' | 'completed'

/**
 * Categorized tasks for display
 */
export interface CategorizedTasks {
  today: ScheduledTaskItem[]      // Available tasks for today
  upcoming: ScheduledTaskItem[]   // Locked future tasks
  completed: ScheduledTaskItem[]  // Already completed tasks
  overdue: ScheduledTaskItem[]    // Past unlocked but not completed
}

/**
 * Get the current day number since assignment start
 */
export function getDaysSinceStart(assignedAt: string | Date): number {
  const startDate = new Date(assignedAt)
  startDate.setHours(0, 0, 0, 0)

  const today = new Date()
  today.setHours(0, 0, 0, 0)

  const diffTime = today.getTime() - startDate.getTime()
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24))

  return diffDays + 1 // Day 1 starts on assignment date
}

/**
 * Get the current week number since assignment start
 */
export function getWeeksSinceStart(assignedAt: string | Date): number {
  const days = getDaysSinceStart(assignedAt)
  return Math.ceil(days / 7)
}

/**
 * Check if a task is unlocked based on organization type and schedule
 */
export function isTaskUnlocked(
  task: ScheduledTaskItem,
  assignedAt: string | Date,
  organizationType: ProgramOrganizationType = 'day_based'
): boolean {
  // If no scheduling info, task is always available
  if (!task.scheduled_day && !task.scheduled_week && !task.scheduled_date) {
    return true
  }

  switch (organizationType) {
    case 'day_based': {
      const currentDay = getDaysSinceStart(assignedAt)
      return (task.scheduled_day || 1) <= currentDay
    }

    case 'week_based': {
      const currentWeek = getWeeksSinceStart(assignedAt)
      return (task.scheduled_week || 1) <= currentWeek
    }

    case 'calendar_based': {
      if (!task.scheduled_date) return true
      const scheduledDate = new Date(task.scheduled_date)
      scheduledDate.setHours(0, 0, 0, 0)
      const today = new Date()
      today.setHours(0, 0, 0, 0)
      return scheduledDate <= today
    }

    default:
      return true
  }
}

/**
 * Check if a task is for today specifically (not just unlocked)
 */
export function isTaskForToday(
  task: ScheduledTaskItem,
  assignedAt: string | Date,
  organizationType: ProgramOrganizationType = 'day_based'
): boolean {
  switch (organizationType) {
    case 'day_based': {
      const currentDay = getDaysSinceStart(assignedAt)
      return (task.scheduled_day || 1) === currentDay
    }

    case 'week_based': {
      const currentWeek = getWeeksSinceStart(assignedAt)
      return (task.scheduled_week || 1) === currentWeek
    }

    case 'calendar_based': {
      if (!task.scheduled_date) return false
      const scheduledDate = new Date(task.scheduled_date)
      scheduledDate.setHours(0, 0, 0, 0)
      const today = new Date()
      today.setHours(0, 0, 0, 0)
      return scheduledDate.getTime() === today.getTime()
    }

    default:
      return true
  }
}

/**
 * Get the unlock date/label for a locked task
 */
export function getUnlockLabel(
  task: ScheduledTaskItem,
  assignedAt: string | Date,
  organizationType: ProgramOrganizationType = 'day_based'
): string {
  switch (organizationType) {
    case 'day_based': {
      const taskDay = task.scheduled_day || 1
      const currentDay = getDaysSinceStart(assignedAt)
      const daysUntil = taskDay - currentDay

      if (daysUntil === 1) return 'Tomorrow'
      if (daysUntil <= 7) return `In ${daysUntil} days`
      return `Day ${taskDay}`
    }

    case 'week_based': {
      const taskWeek = task.scheduled_week || 1
      return `Week ${taskWeek}`
    }

    case 'calendar_based': {
      if (!task.scheduled_date) return ''
      const date = new Date(task.scheduled_date)
      const today = new Date()
      const tomorrow = new Date(today)
      tomorrow.setDate(tomorrow.getDate() + 1)

      if (date.toDateString() === tomorrow.toDateString()) {
        return 'Tomorrow'
      }
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric'
      })
    }

    default:
      return ''
  }
}

/**
 * Get the task status
 */
export function getTaskStatus(
  task: ScheduledTaskItem,
  assignedAt: string | Date,
  organizationType: ProgramOrganizationType = 'day_based'
): TaskStatus {
  // Completed tasks
  if (task.progress?.completed_at) {
    return 'completed'
  }

  // Check if unlocked
  if (isTaskUnlocked(task, assignedAt, organizationType)) {
    return 'available'
  }

  return 'locked'
}

/**
 * Categorize tasks into today/upcoming/completed/overdue
 */
export function categorizeTasks(
  tasks: ScheduledTaskItem[],
  assignedAt: string | Date,
  organizationType: ProgramOrganizationType = 'day_based'
): CategorizedTasks {
  const result: CategorizedTasks = {
    today: [],
    upcoming: [],
    completed: [],
    overdue: [],
  }

  for (const task of tasks) {
    const status = getTaskStatus(task, assignedAt, organizationType)

    if (status === 'completed') {
      result.completed.push(task)
    } else if (status === 'locked') {
      result.upcoming.push(task)
    } else {
      // Available task - check if it's for today or overdue
      if (isTaskForToday(task, assignedAt, organizationType)) {
        result.today.push(task)
      } else {
        // Unlocked but not for today = overdue or just generally available
        result.overdue.push(task)
      }
    }
  }

  // Sort upcoming by schedule
  result.upcoming.sort((a, b) => {
    if (organizationType === 'day_based') {
      return (a.scheduled_day || 0) - (b.scheduled_day || 0)
    }
    if (organizationType === 'week_based') {
      return (a.scheduled_week || 0) - (b.scheduled_week || 0)
    }
    if (organizationType === 'calendar_based') {
      const dateA = a.scheduled_date ? new Date(a.scheduled_date).getTime() : 0
      const dateB = b.scheduled_date ? new Date(b.scheduled_date).getTime() : 0
      return dateA - dateB
    }
    return 0
  })

  // Sort completed by completion date (most recent first)
  result.completed.sort((a, b) => {
    const dateA = a.progress?.completed_at ? new Date(a.progress.completed_at).getTime() : 0
    const dateB = b.progress?.completed_at ? new Date(b.progress.completed_at).getTime() : 0
    return dateB - dateA
  })

  return result
}

/**
 * Get schedule label for a task (Day 1, Week 2, etc.)
 */
export function getScheduleLabel(
  task: ScheduledTaskItem,
  organizationType: ProgramOrganizationType = 'day_based'
): string {
  switch (organizationType) {
    case 'day_based':
      return task.scheduled_day ? `Day ${task.scheduled_day}` : ''

    case 'week_based':
      return task.scheduled_week ? `Week ${task.scheduled_week}` : ''

    case 'calendar_based':
      if (!task.scheduled_date) return ''
      const date = new Date(task.scheduled_date)
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric'
      })

    default:
      return ''
  }
}

/**
 * Get current period label (Day X of Y, Week X of Y)
 */
export function getCurrentPeriodLabel(
  assignedAt: string | Date,
  organizationType: ProgramOrganizationType = 'day_based',
  totalDays?: number,
  totalWeeks?: number
): string {
  switch (organizationType) {
    case 'day_based': {
      const currentDay = getDaysSinceStart(assignedAt)
      if (totalDays) {
        return `Day ${currentDay} of ${totalDays}`
      }
      return `Day ${currentDay}`
    }

    case 'week_based': {
      const currentWeek = getWeeksSinceStart(assignedAt)
      if (totalWeeks) {
        return `Week ${currentWeek} of ${totalWeeks}`
      }
      return `Week ${currentWeek}`
    }

    case 'calendar_based': {
      return new Date().toLocaleDateString('en-US', {
        weekday: 'long',
        month: 'long',
        day: 'numeric'
      })
    }

    default:
      return ''
  }
}
